import { create } from 'zustand';
import type {
  ExtractedData,
  FieldSelection,
  SelectedFields,
  ValidationSession,
  ValidationSessionStatus,
} from '../types';

/**
 * Validation Store State
 */
interface ValidationState {
  // Session state
  sessionId: string | null;
  documentId: string | null;
  transactionId: string | null;
  extractedData: ExtractedData | null;
  selectedFields: SelectedFields;
  status: ValidationSessionStatus | null;
  expiresAt: string | null;
  documentUrl: string | null;

  // UI state
  expandedSections: Set<string>;
  isDirty: boolean;
  isSubmitting: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Validation Store Actions
 */
interface ValidationActions {
  // Session actions
  setSession: (session: ValidationSession) => void;
  clearSession: () => void;

  // Field selection actions
  toggleField: (category: 'transaction' | 'loanTerms' | 'parties', fieldKey: string) => void;
  setFieldValue: <T>(
    category: 'transaction' | 'loanTerms' | 'parties',
    fieldKey: string,
    value: T,
  ) => void;

  // Line item actions
  toggleLineItem: (section: string, itemIndex: number) => void;
  setLineItemField: <T>(section: string, itemIndex: number, fieldKey: string, value: T) => void;

  // Section actions
  toggleSection: (section: string) => void;
  expandAllSections: () => void;
  collapseAllSections: () => void;

  // UI state actions
  setIsSubmitting: (isSubmitting: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Utility actions
  resetSelections: () => void;
  selectAllFields: () => void;
  deselectAllFields: () => void;
}

/**
 * Helper function to create a field selection object
 */
function createFieldSelection<T>(value: T): FieldSelection<T> {
  return {
    include: true,
    value,
    originalValue: value,
    wasModified: false,
  };
}

/**
 * Create initial selected fields from extracted data
 */
function createInitialSelectedFields(extractedData: ExtractedData): SelectedFields {
  const selectedFields: SelectedFields = {};

  // Transaction fields - include all possible fields
  selectedFields.transaction = {};
  if (extractedData.propertyAddress) {
    selectedFields.transaction.propertyAddress = createFieldSelection(
      extractedData.propertyAddress,
    );
  }
  if (extractedData.salePrice) {
    selectedFields.transaction.salePrice = createFieldSelection(extractedData.salePrice);
  }
  if (extractedData.closingDate) {
    selectedFields.transaction.closingDate = createFieldSelection(extractedData.closingDate);
  }
  if (extractedData.disbursementDate) {
    selectedFields.transaction.disbursementDate = createFieldSelection(
      extractedData.disbursementDate,
    );
  }
  if (extractedData.fileNumber) {
    selectedFields.transaction.fileNumber = createFieldSelection(extractedData.fileNumber);
  }
  if (extractedData.loanId) {
    selectedFields.transaction.loanId = createFieldSelection(extractedData.loanId);
  }
  if (extractedData.mic) {
    selectedFields.transaction.mic = createFieldSelection(extractedData.mic);
  }

  // Loan terms fields - include ALL possible fields from LoanTermsSection
  selectedFields.loanTerms = {};
  if (extractedData.loanAmount) {
    selectedFields.loanTerms.loanAmount = createFieldSelection(extractedData.loanAmount);
  }
  if (extractedData.interestRate !== undefined) {
    selectedFields.loanTerms.interestRate = createFieldSelection(extractedData.interestRate);
  }
  if (extractedData.loanTerm) {
    selectedFields.loanTerms.loanTerm = createFieldSelection(extractedData.loanTerm);
  }
  if (extractedData.loanType) {
    selectedFields.loanTerms.loanType = createFieldSelection(extractedData.loanType);
  }
  if (extractedData.loanPurpose) {
    selectedFields.loanTerms.loanPurpose = createFieldSelection(extractedData.loanPurpose);
  }
  if (extractedData.loanProduct) {
    selectedFields.loanTerms.loanProduct = createFieldSelection(extractedData.loanProduct);
  }
  if (extractedData.prepaymentPenalty !== undefined) {
    selectedFields.loanTerms.prepaymentPenalty = createFieldSelection(
      extractedData.prepaymentPenalty,
    );
  }
  if (extractedData.prepaymentPenaltyDetails) {
    selectedFields.loanTerms.prepaymentPenaltyDetails = createFieldSelection(
      extractedData.prepaymentPenaltyDetails,
    );
  }
  if (extractedData.balloonPayment !== undefined) {
    selectedFields.loanTerms.balloonPayment = createFieldSelection(extractedData.balloonPayment);
  }
  if (extractedData.balloonPaymentAmount) {
    selectedFields.loanTerms.balloonPaymentAmount = createFieldSelection(
      extractedData.balloonPaymentAmount,
    );
  }
  if (extractedData.balloonPaymentDueDate) {
    selectedFields.loanTerms.balloonPaymentDueDate = createFieldSelection(
      extractedData.balloonPaymentDueDate,
    );
  }
  // Projected Payments
  if (extractedData.monthlyPrincipalAndInterest) {
    selectedFields.loanTerms.monthlyPrincipalAndInterest = createFieldSelection(
      extractedData.monthlyPrincipalAndInterest,
    );
  }
  extractedData.projectedPayments?.forEach((payment, index) => {
    if (payment.principalAndInterest) {
      selectedFields.loanTerms![`projectedPayment_${index}_pni`] = createFieldSelection(
        payment.principalAndInterest,
      );
    }
    if (payment.mortgageInsurance) {
      selectedFields.loanTerms![`projectedPayment_${index}_mi`] = createFieldSelection(
        payment.mortgageInsurance,
      );
    }
    if (payment.estimatedEscrow) {
      selectedFields.loanTerms![`projectedPayment_${index}_escrow`] = createFieldSelection(
        payment.estimatedEscrow,
      );
    }
    if (payment.estimatedTotal) {
      selectedFields.loanTerms![`projectedPayment_${index}_total`] = createFieldSelection(
        payment.estimatedTotal,
      );
    }
  });
  if (extractedData.estimatedTaxesInsuranceAssessments) {
    selectedFields.loanTerms.estimatedTaxesInsuranceAssessments = createFieldSelection(
      extractedData.estimatedTaxesInsuranceAssessments,
    );
  }
  if (extractedData.estimatedEscrowPayment) {
    selectedFields.loanTerms.estimatedEscrowPayment = createFieldSelection(
      extractedData.estimatedEscrowPayment,
    );
  }
  if (extractedData.hasEscrow !== undefined) {
    selectedFields.loanTerms.hasEscrow = createFieldSelection(extractedData.hasEscrow);
  }
  if (extractedData.escrowedItems && extractedData.escrowedItems.length > 0) {
    selectedFields.loanTerms.escrowedItems = createFieldSelection(
      extractedData.escrowedItems.join(', '),
    );
  }
  // Loan Calculations
  if (extractedData.totalPayments) {
    selectedFields.loanTerms.totalPayments = createFieldSelection(extractedData.totalPayments);
  }
  if (extractedData.financeCharge) {
    selectedFields.loanTerms.financeCharge = createFieldSelection(extractedData.financeCharge);
  }
  if (extractedData.apr) {
    selectedFields.loanTerms.apr = createFieldSelection(extractedData.apr);
  }
  if (extractedData.totalInterestPercentage) {
    selectedFields.loanTerms.totalInterestPercentage = createFieldSelection(
      extractedData.totalInterestPercentage,
    );
  }
  if (extractedData.cashToClose) {
    selectedFields.loanTerms.cashToClose = createFieldSelection(extractedData.cashToClose);
  }

  // Parties fields
  const partiesArray: Array<{ role: string; [key: string]: FieldSelection | string }> = [];
  if (extractedData.borrowerName) {
    partiesArray.push({ role: 'borrower', name: createFieldSelection(extractedData.borrowerName) });
  }
  if (extractedData.borrowerAddress) {
    partiesArray.push({
      role: 'borrower_address',
      address: createFieldSelection(extractedData.borrowerAddress),
    });
  }
  if (extractedData.sellerName) {
    partiesArray.push({ role: 'seller', name: createFieldSelection(extractedData.sellerName) });
  }
  if (extractedData.lenderName) {
    partiesArray.push({ role: 'lender', name: createFieldSelection(extractedData.lenderName) });
  }
  if (extractedData.settlementAgentName) {
    partiesArray.push({
      role: 'settlement_agent',
      name: createFieldSelection(extractedData.settlementAgentName),
    });
  }
  // Add dynamic party fields
  extractedData.parties?.forEach((party, _index) => {
    const partyObj: { role: string; [key: string]: FieldSelection | string } = {
      role: party.party_type,
      name: createFieldSelection(party.name),
    };
    if (party.email) {
      partyObj.email = createFieldSelection(party.email);
    }
    if (party.phone) {
      partyObj.phone = createFieldSelection(party.phone);
    }
    if (party.nmls) {
      partyObj.nmls = createFieldSelection(party.nmls);
    }
    if (party.license_id) {
      partyObj.license = createFieldSelection(party.license_id);
    }
    partiesArray.push(partyObj);
  });
  selectedFields.parties = partiesArray;

  // Line items - group by section to match the LineItemsSection structure
  if (extractedData.lineItems && extractedData.lineItems.length > 0) {
    // Group items by section first
    const groupedBySection = extractedData.lineItems.reduce(
      (acc, item) => {
        if (!acc[item.section]) {
          acc[item.section] = [];
        }
        (acc[item.section] as typeof extractedData.lineItems)!.push(item);
        return acc;
      },
      {} as Record<string, typeof extractedData.lineItems>,
    );

    selectedFields.lineItems = Object.entries(groupedBySection).map(([section, items]) => ({
      section,
      items: items.map((item) => ({
        lineNumber: item.lineNumber,
        include: true,
        fields: {
          label: createFieldSelection(item.label || ''),
          amount: createFieldSelection(item.amount),
        },
      })),
    }));
  }

  return selectedFields;
}

/**
 * Validation Store
 */
export const useValidationStore = create<ValidationState & ValidationActions>()((set, get) => ({
  // Initial state
  sessionId: null,
  documentId: null,
  transactionId: null,
  extractedData: null,
  selectedFields: {},
  status: null,
  expiresAt: null,
  documentUrl: null,
  expandedSections: new Set(['transaction', 'loanTerms', 'lineItems']),
  isDirty: false,
  isSubmitting: false,
  isLoading: false,
  error: null,

  // Session actions
  setSession: (session) => {
    set({
      sessionId: session.sessionId,
      documentId: session.documentId,
      transactionId: session.transactionId,
      extractedData: session.extractedData,
      selectedFields: session.selectedFields || createInitialSelectedFields(session.extractedData),
      status: session.status,
      expiresAt: session.expiresAt,
      documentUrl: session.documentUrl || null,
      isDirty: false,
      error: null,
    });
  },

  clearSession: () => {
    set({
      sessionId: null,
      documentId: null,
      transactionId: null,
      extractedData: null,
      selectedFields: {},
      status: null,
      expiresAt: null,
      documentUrl: null,
      isDirty: false,
      error: null,
    });
  },

  // Field selection actions
  toggleField: (category, fieldKey) => {
    const { selectedFields } = get();
    const categoryData = selectedFields[category] as
      | Record<string, FieldSelection<unknown>>
      | undefined;

    // Handle parties (Array) differently from transaction/loanTerms (Record)
    if (category === 'parties') {
      // Parties are arrays, toggle not supported
      return;
    }

    if (!categoryData || Array.isArray(categoryData)) {
      return;
    }

    const categoryFields = categoryData as Record<string, FieldSelection>;
    if (!(fieldKey in categoryFields)) {
      return;
    }

    const field = categoryFields[fieldKey];
    if (!field) return;

    const updatedFields = {
      ...categoryFields,
      [fieldKey]: {
        ...field,
        include: !field.include,
        wasModified: true,
      },
    };

    set({
      selectedFields: {
        ...selectedFields,
        [category]: updatedFields,
      },
      isDirty: true,
    });
  },

  setFieldValue: <T>(
    category: 'transaction' | 'loanTerms' | 'parties',
    fieldKey: string,
    value: T,
  ) => {
    const { selectedFields } = get();
    const categoryData = selectedFields[category] as
      | Record<string, FieldSelection<unknown>>
      | undefined;

    // Handle parties (Array) differently from transaction/loanTerms (Record)
    if (category === 'parties') {
      // Parties are arrays, setFieldValue not supported
      return;
    }

    if (!categoryData || Array.isArray(categoryData)) {
      return;
    }

    const categoryFields = categoryData as Record<string, FieldSelection>;
    const field = categoryFields[fieldKey] as FieldSelection<T> | undefined;
    const newField: FieldSelection<T> = {
      ...(field || { include: true, wasModified: false }),
      value,
      wasModified: value !== field?.originalValue,
    };
    const updatedFields = {
      ...categoryFields,
      [fieldKey]: newField,
    };

    set({
      selectedFields: {
        ...selectedFields,
        [category]: updatedFields,
      },
      isDirty: true,
    });
  },

  // Line item actions
  toggleLineItem: (section, itemIndex) => {
    const { selectedFields } = get();
    const sectionData = selectedFields.lineItems?.find((s) => s.section === section);

    if (!sectionData || !sectionData.items[itemIndex]) {
      return;
    }

    const item = sectionData.items[itemIndex];
    const updatedItems = [...sectionData.items];
    updatedItems[itemIndex] = {
      ...item,
      include: !item.include,
    };

    const updatedLineItems = selectedFields.lineItems?.map((s) =>
      s.section === section ? { ...s, items: updatedItems } : s,
    );

    set({
      selectedFields: {
        ...selectedFields,
        lineItems: updatedLineItems,
      },
      isDirty: true,
    });
  },

  setLineItemField: <T>(section: string, itemIndex: number, fieldKey: string, value: T) => {
    const { selectedFields } = get();
    const sectionData = selectedFields.lineItems?.find((s) => s.section === section);

    if (!sectionData || !sectionData.items[itemIndex]) {
      return;
    }

    const item = sectionData.items[itemIndex];
    const updatedItems = [...sectionData.items];
    updatedItems[itemIndex] = {
      ...item,
      fields: {
        ...item.fields,
        [fieldKey]: {
          ...item.fields?.[fieldKey],
          value,
          wasModified: true,
          include: item.fields?.[fieldKey]?.include ?? true,
        },
      },
    } as (typeof updatedItems)[number];

    const updatedLineItems = selectedFields.lineItems?.map((s) =>
      s.section === section ? { ...s, items: updatedItems } : s,
    );

    set({
      selectedFields: {
        ...selectedFields,
        lineItems: updatedLineItems,
      },
      isDirty: true,
    });
  },

  // Section actions
  toggleSection: (section) => {
    const { expandedSections } = get();
    const newExpandedSections = new Set(expandedSections);

    if (newExpandedSections.has(section)) {
      newExpandedSections.delete(section);
    } else {
      newExpandedSections.add(section);
    }

    set({ expandedSections: newExpandedSections });
  },

  expandAllSections: () => {
    set({ expandedSections: new Set(['transaction', 'loanTerms', 'lineItems', 'parties']) });
  },

  collapseAllSections: () => {
    set({ expandedSections: new Set() });
  },

  // UI state actions
  setIsSubmitting: (isSubmitting) => {
    set({ isSubmitting });
  },

  setIsLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  // Utility actions
  resetSelections: () => {
    const { extractedData } = get();
    if (!extractedData) {
      return;
    }

    set({
      selectedFields: createInitialSelectedFields(extractedData),
      isDirty: true,
    });
  },

  selectAllFields: () => {
    const { selectedFields } = get();

    const selectFieldRecursive = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(selectFieldRecursive);
      }
      if (obj && typeof obj === 'object') {
        // Handle objects with 'include' property
        if ('include' in obj) {
          return { ...obj, include: true };
        }
        // Recursively handle nested objects
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
          result[key] = selectFieldRecursive(value);
        }
        return result;
      }
      return obj;
    };

    const newSelectedFields = selectFieldRecursive(selectedFields);

    console.log('[validationStore] selectAllFields:', {
      before: selectedFields,
      after: newSelectedFields,
    });

    set({
      selectedFields: newSelectedFields,
      isDirty: true,
    });
  },

  deselectAllFields: () => {
    const { selectedFields } = get();

    const deselectFieldRecursive = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(deselectFieldRecursive);
      }
      if (obj && typeof obj === 'object') {
        // Handle objects with 'include' property
        if ('include' in obj) {
          return { ...obj, include: false };
        }
        // Recursively handle nested objects
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
          result[key] = deselectFieldRecursive(value);
        }
        return result;
      }
      return obj;
    };

    const newSelectedFields = deselectFieldRecursive(selectedFields);

    console.log('[validationStore] deselectAllFields:', {
      before: selectedFields,
      after: newSelectedFields,
    });

    set({
      selectedFields: newSelectedFields,
      isDirty: true,
    });
  },
}));
